import React, { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { App } from '../../../models/app';
import { ActionButtonProps, AppActions } from './types';
import { useUnInstallAppMutation } from '../../../redux/api/appMarket-api-slice';
import LoadingButton from '../../Common/LoadingButton';
import { canUninstallApp } from '../../../component-helpers/app-actions';
import { UninstallAppResponse } from '../../../models/post-req-responses-payloads';
import { ButtonVariant } from '@clover/web-lib-core/components';
import { TextButton } from '../../Common/TextButton';
import { usePermission } from '../../../hooks/usePermission';
import { AppMarketPermissions } from '../../../models/enums';
import { useCollectionsRelatedToApp } from '../../../hooks/useCollectionsRelatedToApp';
import { NotificationVariant } from '../../Common/Notifications/types';


interface ComponentProps extends ActionButtonProps<UninstallAppResponse> {
  app: App;
}

export const UninstallAppButton: React.FC<ComponentProps> = (
  {
    app,
    actionCallBack,
    buttonVariant,
    isMenuItem,
    text,
    ...rest
  }): Nullable<JSX.Element> => {
  const { id: appId } = app;
  const { t } = useTranslation();
  const [unInstallApp] = useUnInstallAppMutation();
  const [processing, setProcessing] = useState<boolean>(false);
  const { enabled: hasInstallUninstallPermission } = usePermission(AppMarketPermissions.InstallUninstallApps);
  const collectionsRelatedToApp = useCollectionsRelatedToApp(appId);
  const buttonText = text || t(AppActions.Uninstall);
  const isDisabled = !canUninstallApp(app, hasInstallUninstallPermission);

  useLayoutEffect(() => {
    setProcessing(false); // let the loader spin until new action comes in
  }, [app]);

  const uninstallApp = async () => {
    let reqResponse;
    setProcessing(true);
    reqResponse = await unInstallApp({ appId, collectionsRelatedToApp });
    actionCallBack?.({
      reqResponse,
      successNotification: {
        variant: NotificationVariant.Green,
        title: 'app_actions.banners.success_app_uninstalled'
      }
    });
    setTimeout(() => {
      setProcessing(false);
    }, 3000);
  };

  return (
    isMenuItem ?
      <TextButton onClick={uninstallApp} text={buttonText!} {...rest}/> :
      <LoadingButton
        variant={buttonVariant !== undefined ? buttonVariant : ButtonVariant.PRIMARY}
        text={buttonText}
        isLoading={processing}
        onClick={uninstallApp}
        isDisabled={processing || isDisabled}
        {...rest}
      />
  );
};
